/* eslint-disable @typescript-eslint/indent */
import { Button, Container, Slider, Tooltip, Typography } from '@pwskills/rachnaui';
import CheckMarkIcon from '@/assets/icons/CheckMarkIcon';
import { useContext, useEffect, useMemo, useState } from 'react';
import { getPriceDetails, getRegistrationStatus } from '@/utils/helper/helperFunction';
import { ICoursePlans } from '@/interfaces/courseDescription';
// import { accountUrl } from '@/utils/common/env.constants';
import CourseDescriptionContext from '@/context/CourseDescriptionContext';
// import { useUTMParameters } from '@pwskills/rachnaui/hooks';
import { useRouter } from 'next/router';
import { REGISTRATION_STATUS } from '@/utils/common/common.constants';
import useIsCourseBought from '@/hooks/useIsCourseBought';
import useUserCourseDetails from '@/store/userCoursesDetails';
import { useUTMParameters } from '@pwskills/rachnaui/hooks';
import { GAevent } from '@/utils/firebase.utils';
import { sendSkBuyNowClickedEvent } from '@/utils/GAEventFunction';
import { usePWAuthWebSDK } from '@/hooks/usePWAuthWebSDK';
import { AuthEnums } from '@/GlobalEnums/enums';
import { getNavigationContext } from '@/utils/navigationContext';
import { ProductListNavLink } from '@/utils/homePage/home.constants';
import Headers from '../Header';
import ProfessionalThemeHeader from '../Header/ProfessionalThemeHeader';
import useUserDetails from '../../../store/userDetails';
import { handleCourseExpiryLogic } from './ProgramOutcomes/utils';
// import { handleCourseExpiryLogic } from './ProgramOutcomes/utils';

const OFFLINE_MODES = ['OFFLINE', 'RECORDED'];
const TRIAL_MODE = 'TRIAL';
const isTrialModePlan = (p: ICoursePlans) => (p.batchMode || '').toUpperCase() === TRIAL_MODE;
// trial plans can be paid too; free CTA only when pricing says so
const isFreeTrialPlan = (p: ICoursePlans) => isTrialModePlan(p) && Boolean(p.pricing?.isFree);

// PlansSection
const PlansSection = ({ isAmateur = true }: { isAmateur?: boolean }) => {
  const { courseDetails } = useContext(CourseDescriptionContext);
  // const { isUserPresenceFilled } = useAuthentication();
  const { coursePlansDetails } = courseDetails?.courseMetaDetails || {};
  const [isCourseBought, setIsCourseBought] = useState<boolean>(false);

  // const { isLoggedIn } = useContext(UserDetailsContext);
  const { userDetails, userIsLoggedIn: isLoggedIn, isUserPaid } = useUserDetails();
  const utmQuery = useUTMParameters();
  const router = useRouter();
  const navigationContext = getNavigationContext(router.asPath);
  const [currentSelectedPlanId, setCurrentSelectedPlanId] = useState<string | null>(null);
  const { checkIsCourseBought, isCourseBoughtForPlan } = useIsCourseBought();
  const { userCoursesMap } = useUserCourseDetails();

  const hasOnlinePlans = useMemo(
    () => coursePlansDetails?.some((p) => (p.batchMode || '').toUpperCase() === 'ONLINE') ?? false,
    [coursePlansDetails]
  );
  const hasOfflinePlans = useMemo(
    () =>
      coursePlansDetails?.some((p) => OFFLINE_MODES.includes((p.batchMode || '').toUpperCase())) ??
      false,
    [coursePlansDetails]
  );
  const hasTrialPlans = useMemo(
    () =>
      coursePlansDetails?.some((p) => (p.batchMode || '').toUpperCase() === TRIAL_MODE) ?? false,
    [coursePlansDetails]
  );

  // toggle only appears when plans span more than one mode
  const availableModes = useMemo(() => {
    const modes: string[] = [];
    if (hasTrialPlans) modes.push(TRIAL_MODE);
    if (hasOnlinePlans) modes.push('ONLINE');
    if (hasOfflinePlans) modes.push('OFFLINE');
    return modes.length > 1 ? modes : [];
  }, [hasOnlinePlans, hasOfflinePlans, hasTrialPlans]);
  const showModeToggle = availableModes.length > 1;
  const [selectedMode, setSelectedMode] = useState<string>('');

  const isOfflineCourse = useMemo(() => {
    const offlineSlugs = (
      ProductListNavLink.find((item) => item.type === 'offline_courses')?.options || []
    ).map((opt) => {
      const parts = new URL(opt.href).pathname.split('/').filter(Boolean);
      return parts[parts.length - 1]?.toLowerCase();
    });
    return offlineSlugs.includes((courseDetails?.slug || '').toLowerCase());
  }, [courseDetails?.slug]);

  useEffect(() => {
    if (availableModes.length > 0 && !selectedMode) {
      // plan_mode query param (e.g. from trial nav dropdown) preselects the tab when it exists
      const requestedMode = ((router?.query?.plan_mode as string) || '').toUpperCase();
      setSelectedMode(
        availableModes.includes(requestedMode)
          ? requestedMode
          : isOfflineCourse && hasOfflinePlans
          ? 'OFFLINE'
          : availableModes[0]
      );
    }
  }, [availableModes, selectedMode, router?.query?.plan_mode]);

  const filteredPlans = useMemo(() => {
    if (!coursePlansDetails) return [];
    if (!showModeToggle) return coursePlansDetails;
    const mode = (p: ICoursePlans) => (p.batchMode || '').toUpperCase();
    if (selectedMode === 'OFFLINE') {
      return coursePlansDetails.filter((p) => OFFLINE_MODES.includes(mode(p)));
    }
    if (selectedMode === TRIAL_MODE) {
      return coursePlansDetails.filter((p) => mode(p) === TRIAL_MODE);
    }
    return coursePlansDetails.filter((p) => mode(p) === 'ONLINE');
  }, [coursePlansDetails, showModeToggle, selectedMode]);

  useEffect(() => {
    if (filteredPlans.length > 0) {
      const recommended = filteredPlans.find((p) => p.isRecommended);
      setCurrentSelectedPlanId(recommended ? recommended._id : filteredPlans[0]._id);
    }
  }, [selectedMode, filteredPlans]);

  const sendGAevent = (source: string) => {
    const GAEventData = {
      source,
      ...utmQuery,
    };
    GAevent('sk_login_page_visit', GAEventData);
  };

  const planPricing = courseDetails?.courseMetaDetails?.pricing || courseDetails?.pricing;
  const planPriceDetails = getPriceDetails(planPricing);

  const { initApp } = usePWAuthWebSDK({
    sdkProps: {
      themeFor: 'skills',
      onGoogleSignIn: () => {
        // your logic
      },
      googleClientId: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '',
      cameFrom: AuthEnums.BLOG,
      ctaName: AuthEnums.LOGIN,
      formLocation: 'course_description',
      course_batch_name: courseDetails?.name || courseDetails?.title,
      course_batch_slug: courseDetails?.slug,
      course_batch_category: courseDetails?.parentCategoryDetails?.title,
      course_batch_id: courseDetails?._id,
      course_batch_cohort_id:
        courseDetails?.batchCategoryId ||
        (courseDetails?.courseMetaDetails as any)?.cohortId?._id ||
        (courseDetails?.courseMetaDetails as any)?.cohortId,
      price:
        typeof planPriceDetails?.currentPrice === 'number'
          ? planPriceDetails.currentPrice
          : undefined,
      discount_percentage:
        typeof planPriceDetails?.discount === 'number' ? planPriceDetails.discount : undefined,
      is_free: planPricing?.isFree,
    },
  });

  const handleClickWithRedirect = (redirectUrl: string) => {
    if (isLoggedIn) {
      router.push(redirectUrl);
      return;
    }
    initApp(redirectUrl);
    sendGAevent('Buy Now');
  };
  useEffect(() => {
    if (coursePlansDetails) {
      // Find the plan with recommended set to true
      const recommendedPlan = coursePlansDetails.find((plan) => plan.isRecommended === true);

      // If a recommended plan is found, set it as the default current plan
      if (recommendedPlan) {
        setCurrentSelectedPlanId(recommendedPlan._id);
      } else {
        // Handle the case where no recommended plan is found
        // console.error('No recommended plan found.');
      }
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && coursePlansDetails && userCoursesMap) {
      // owning a trial must not lock the paid plans
      setIsCourseBought(
        isCourseBoughtForPlan(coursePlansDetails.filter((p) => !isTrialModePlan(p)))
      );
    }
  }, [isLoggedIn, coursePlansDetails, userCoursesMap]);

  const onBuyCoursePlan = (courseId: string, planName: string, offlineCourseId?: string) => {
    const offlineCourseQuery = offlineCourseId
      ? `&offline_course_id=${encodeURIComponent(offlineCourseId)}`
      : '';
    if (isLoggedIn && !isCourseBought) {
      // need to check source param and position --------
      router.push(
        `/checkout?course_id=${courseId}&source=description_page&plan_name=${planName}${offlineCourseQuery}`
      );
    } else {
      handleClickWithRedirect(
        `/checkout?course_id=${courseId}&plan_name=${planName}${offlineCourseQuery}`
      );
    }
  };

  const handlePlanButtonClick = (plan: ICoursePlans) => {
    if (!checkIsCourseBought(plan?._id)) {
      const priceDetails = getPriceDetails(plan.pricing);
      const planPriceValue =
        typeof priceDetails?.currentPrice === 'number'
          ? Number(priceDetails.currentPrice)
          : plan?.pricing?.IN || plan?.pricing?.US;

      const categoryNameFromQuery =
        (router?.query?.category_name as string) || navigationContext.categoryName || '';
      const categorySlugFromQuery =
        (router?.query?.category_slug as string) || navigationContext.categorySlug || '';
      const categorySlugFromPath = (() => {
        const asPath = router?.asPath || '';
        const [pathOnly] = asPath.split('?');
        const segments = pathOnly.split('/').filter(Boolean);
        return segments.length >= 2 ? segments[0] : '';
      })();

      sendSkBuyNowClickedEvent({
        userData: userDetails,
        isLoggedIn,
        isUserPaid,
        user_account_type:
          typeof isUserPaid === 'boolean' ? (isUserPaid ? 'Paid' : 'Free') : undefined,
        page_path: router?.pathname,
        page_referrer_name: (router?.query?.from as string) || navigationContext.from || undefined,
        page_referrer_cta: (router?.query?.clickText as string) || undefined,
        page_referrer_widget_name: (router?.query?.section as string) || undefined,
        click_text: isFreeTrialPlan(plan) ? 'Enroll For Free' : 'Buy now',
        plan_type: plan?.name,
        course_name: courseDetails?.title || courseDetails?.name || '',
        course_slug: courseDetails?.slug || '',
        course_category:
          courseDetails?.categoryDetails?.title ||
          courseDetails?.categoryDetails?.name ||
          courseDetails?.categoryDetails?.slug ||
          categoryNameFromQuery ||
          categorySlugFromQuery ||
          categorySlugFromPath ||
          '',
        course_id: plan?.courseId || courseDetails?._id || '',
        cohort_id: courseDetails?.cohortId?._id,
        price: courseDetails?.pricing?.IN || planPriceValue,
        discount_percentage: plan?.pricing?.discount,
        is_free: plan?.pricing?.isFree,
      });
    }

    const offlineCourseId = OFFLINE_MODES.includes((plan.batchMode || '').toUpperCase())
      ? courseDetails?._id
      : undefined;
    onBuyCoursePlan(plan._id, plan.name, offlineCourseId);
  };

  return (
    <section data-title="Plans" id="plans" className=" target:bg-white bg-[#F8F8F8] ">
      <Container className="medium:!px-4 large:!px-20 py-9 medium:py-14 flex flex-col gap-4 medium:!gap-10 large:!gap-[52px] ">
        {isAmateur && (
          <header className="flex justify-center text-[#3D3D3D]">
            <Headers
              title="Choose Your Plan"
              classes="flex w-full !min-w-full justify-center text-gray-850 medium:w-auto text-center !self-center !w-[50%]  medium:!w-full  "
            />
          </header>
        )}
        {!isAmateur && (
          <ProfessionalThemeHeader title="Customize Your Growth: Course Plan Options" />
        )}
        {showModeToggle && (
          <div className="flex justify-center">
            <div className="flex bg-gray-100 rounded-full p-2">
              {availableModes.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  style={{ fontSize: '20px', lineHeight: '30px', fontWeight: 600 }}
                  className={`px-7 py-2 rounded-full transition-all ${
                    selectedMode === mode ? 'bg-white text-orange-500 shadow-md' : 'text-[#3D3D3D]'
                  }`}
                  onClick={() => setSelectedMode(mode)}
                >
                  {mode === TRIAL_MODE
                    ? 'Free Online Courses'
                    : mode.charAt(0) + mode.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-6 large:gap-10">
          <Slider
            className="flex gap-4 large:gap-6"
            wrapperClass="flex flex-col gap-6 "
            iconWrapperClass="!p-0  !mx-0"
            positionOfSliderInMobile="bottom"
          >
            {filteredPlans.map((plan: ICoursePlans) => {
              const priceDetails = getPriceDetails(plan.pricing);
              const { actualPrice, currency, discount, currentPrice } = priceDetails || {};

              return (
                <div
                  className={`!min-w-[93%] medium:!min-w-[332px] large:!min-w-[408px] !max-w-[93%] medium:!max-w-[332px] large:!max-w-[408px] rounded-xl !bg-white px-6 large:px-16 py-8  flex flex-col gap-4 justify-between  border ${
                    currentSelectedPlanId === plan._id ? 'border-orange-500' : 'border-[#EFEFEF]'
                  } `}
                  onClick={() => {
                    setCurrentSelectedPlanId(plan._id);
                  }}
                  key={plan._id}
                >
                  <div className="flex flex-col gap-4">
                    {/* plan header section */}
                    <div className="flex flex-col gap-4">
                      <img
                        src={plan.icon}
                        alt="plan icon"
                        width={32}
                        height={32}
                        className="!w-8 !h-8"
                      />
                      <div className="flex gap-2 items-center">
                        <Typography variant="semi-bold" component="h4" className="text-gray-850">
                          {plan.name}
                        </Typography>
                        {plan.isRecommended && (
                          <Typography
                            variant="regular"
                            component="body-tiny"
                            className="px-4 py-1 text-white bg-[#B07AE5] rounded-[56px]"
                          >
                            Recommended
                          </Typography>
                        )}
                      </div>
                      {plan.showPricingOnCard && (
                        <div className="flex gap-2 items-center">
                          <Typography variant="bold" component="h3" className="text-gray-850">
                            {`${currency} ${Math.ceil(currentPrice as number)} `}
                          </Typography>

                          {discount !== undefined && discount > 0 && (
                            <Typography
                              variant="strikethrough"
                              component="h3"
                              className="text-gray-400"
                            >
                              {actualPrice}
                            </Typography>
                          )}
                        </div>
                      )}
                      {plan.showEMITextOnCard && (
                        <Typography
                          variant="regular"
                          component="body-tiny"
                          className="text-gray-850 whitespace-nowrap"
                        >
                          {plan?.pricing?.emiText}
                        </Typography>
                      )}
                    </div>
                    {/* plan buy now button */}

                    <Tooltip
                      text={getRegistrationStatus(plan.registration)}
                      className={`${
                        plan.registration?.startDate === '' ||
                        getRegistrationStatus(plan.registration) === REGISTRATION_STATUS.OPEN ||
                        isCourseBought
                          ? 'hidden'
                          : ''
                      }`}
                    >
                      <Button
                        variant={`${
                          currentSelectedPlanId === plan._id && !isCourseBought
                            ? 'primary'
                            : 'tertiary'
                        }`}
                        className={`${
                          checkIsCourseBought(plan?._id) &&
                          'bg-green-50 text-green-500 hover:bg-green-50'
                        } w-full`}
                        disabled={
                          checkIsCourseBought(plan?._id) ||
                          isCourseBought ||
                          getRegistrationStatus({
                            startDate: plan?.registrationStartDate,
                            endDate: plan?.registrationEndDate,
                          }) !== REGISTRATION_STATUS.OPEN
                        }
                        onClick={() => handlePlanButtonClick(plan)}
                      >
                        {checkIsCourseBought(plan?._id)
                          ? 'Enrolled'
                          : isFreeTrialPlan(plan)
                          ? 'Enroll For Free'
                          : 'Buy now'}
                      </Button>
                    </Tooltip>
                    {/* plan features */}
                    <div className={`flex flex-col ${plan.onlyFeatures ? 'gap-9' : 'gap-4'}`}>
                      {plan.features &&
                        [
                          ...(plan?.config?.isCourseExpiryEnabled
                            ? [
                                {
                                  _id: '1',
                                  text: 'Extended Access',
                                  subText: `Access to recorded content for ${handleCourseExpiryLogic(
                                    plan
                                  )} months`,
                                  isAvailable: true,
                                },
                              ]
                            : []),

                          ...plan.features,
                        ].map((feature) => {
                          // {plan.features.map((feature) => {
                          return (
                            <div className="flex flex-col" key={feature._id}>
                              {/* if only feature is true, show check mark icon */}
                              <div className="flex gap-1 items-center">
                                {plan.onlyFeatures && (
                                  <div className="!w-6 !h-6">
                                    <CheckMarkIcon
                                      typeOfCheckmark="plans"
                                      color={feature.isAvailable ? '#3E8E2A' : '#D1D1D1'}
                                    />
                                  </div>
                                )}
                                {feature.text && (
                                  <Typography
                                    variant="bold"
                                    component="body-regular"
                                    className={` ${
                                      feature.isAvailable ? 'text-gray-850' : 'text-gray-125'
                                    } p-1`}
                                  >
                                    {feature.text}
                                  </Typography>
                                )}
                              </div>

                              {/* if only feature is false, show sub text */}
                              {!plan.onlyFeatures && feature.text && (
                                <Typography
                                  variant="regular"
                                  component="body-tiny"
                                  className={` ${
                                    feature.isAvailable ? 'text-gray-650' : 'text-gray-125'
                                  } p-1`}
                                >
                                  {feature.subText}
                                </Typography>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 justify-end">
                    <div className="bg-[#EAECEF] h-[1px]" />

                    {/* Nudge */}
                    <div
                      className={`p-3 w-full items-end rounded-lg ${
                        currentSelectedPlanId === plan._id ? 'bg-yellow-50' : 'bg-gray-100'
                      }`}
                    >
                      <Typography
                        variant="regular"
                        component="body-small"
                        className="text-gray-650 mx-auto text-center line-clamp-2"
                      >
                        {plan.nudgeText}
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
          <Typography
            variant="regular"
            component="subheading"
            className="text-gray-650 !text-[14px]"
          >
            <sup>*</sup>Plans consists of{' '}
            <span className="!font-bold text-gray-850">No Cost EMI</span>
          </Typography>
        </div>
      </Container>
    </section>
  );
};

export default PlansSection;
